import { FunctionComponent, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { API_URL } from "../../config";
import useFiles from "../../hooks/useFiles";
import {
  useOpsEmailDistributionDetails,
  useTriggerOpsEmailDistributionOverride,
} from "../../hooks/useOpsEmailDistribution";

const recipientRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parseRecipients = (value: string) =>
  Array.from(
    new Set(
      `${value || ""}`
        .split(/[\n,;]+/)
        .map((entry) => entry.trim().toLowerCase())
        .filter(Boolean),
    ),
  );

const isPreviewable = (fileName: string) => {
  const lower = `${fileName || ""}`.toLowerCase();
  return (
    lower.endsWith(".pdf") ||
    lower.endsWith(".png") ||
    lower.endsWith(".jpg") ||
    lower.endsWith(".jpeg") ||
    lower.endsWith(".webp")
  );
};

const getFileNameFromPath = (value?: string | null) => {
  if (!value) {
    return null;
  }

  const normalized = `${value}`.split("/").filter(Boolean);
  if (!normalized.length) {
    return null;
  }

  return normalized[normalized.length - 1] || null;
};

const buildFileUrl = (flightId: string, fileName: string) =>
  `${`${API_URL}`.replace(/\/+$/, "")}/flights/${encodeURIComponent(
    flightId,
  )}/files/${encodeURIComponent(fileName)}`;

const toAbsoluteApiPath = (pathValue?: string | null) => {
  if (!pathValue) {
    return null;
  }

  const normalizedPath = `${pathValue}`.trim();
  if (!normalizedPath.length) {
    return null;
  }

  if (/^https?:\/\//i.test(normalizedPath)) {
    return normalizedPath;
  }

  const cleanApiBase = `${API_URL}`.replace(/\/+$/, "");
  const baseHasApiSuffix = /\/api$/i.test(cleanApiBase);
  let pathWithSlash = normalizedPath.startsWith("/")
    ? normalizedPath
    : `/${normalizedPath}`;
  pathWithSlash = pathWithSlash.replace(/^\/(?:api\/)+api(\/|$)/i, "/api$1");
  pathWithSlash = pathWithSlash.replace(/^\/api\/api(\/|$)/i, "/api$1");

  if (baseHasApiSuffix && /^\/api(?:\/|$)/i.test(pathWithSlash)) {
    pathWithSlash = pathWithSlash.replace(/^\/api/i, "");
  }

  return `${cleanApiBase}${pathWithSlash}`;
};

const toComparableTimestamp = (value?: string | null) => {
  if (!value) {
    return 0;
  }

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const pickPreferredLeonPdfVersion = (
  versions: LeonPdfVersion[] = [],
) => {
  const withFile = versions
    .filter((version) => Boolean(version?.filePath))
    .slice()
    .sort((left, right) => {
      const timestampDelta =
        toComparableTimestamp(right?.createdAt) -
        toComparableTimestamp(left?.createdAt);
      if (timestampDelta !== 0) {
        return timestampDelta;
      }
      return Number(right?.id || 0) - Number(left?.id || 0);
    });

  const corrected = withFile.find((version) => version.versionType === "corrected");
  if (corrected) {
    return corrected;
  }

  const originalDefault = withFile.find(
    (version) => version.versionType === "original" && version.isDefault,
  );
  if (originalDefault) {
    return originalDefault;
  }

  const original = withFile.find((version) => version.versionType === "original");
  if (original) {
    return original;
  }

  return withFile[0] || null;
};

type SendableFlightFileOption = {
  key: string;
  fileName: string;
  filePath: string | null;
  previewUrl: string | null;
  openUrl: string | null;
  downloadUrl: string | null;
  source: "flight-folder" | "leon-pdf";
  createdAt: string | null;
};

export const buildSendableFlightFileOptions = ({
  flightId,
  files = [],
  leonTransfer = null,
}: {
  flightId: string;
  files?: string[];
  leonTransfer?: FlightDetailLeonTransfer | null;
}): SendableFlightFileOption[] => {
  const optionsByKey = new Map<string, SendableFlightFileOption>();

  for (const fileName of files) {
    const normalizedFileName = `${fileName || ""}`.trim();
    if (!normalizedFileName.length) {
      continue;
    }

    const fileUrl = buildFileUrl(flightId, normalizedFileName);
    optionsByKey.set(normalizedFileName, {
      key: normalizedFileName,
      fileName: normalizedFileName,
      filePath: null,
      previewUrl: isPreviewable(normalizedFileName) ? fileUrl : null,
      openUrl: fileUrl,
      downloadUrl: fileUrl,
      source: "flight-folder",
      createdAt: null,
    });
  }

  for (const version of leonTransfer?.pdfVersions || []) {
    const fileName = getFileNameFromPath(version.filePath);
    if (!fileName) {
      continue;
    }

    const fallbackUrl = buildFileUrl(flightId, fileName);
    const nextOption: SendableFlightFileOption = {
      key: fileName,
      fileName,
      filePath: version.filePath || null,
      previewUrl:
        toAbsoluteApiPath(version.previewPath) ||
        (isPreviewable(fileName) ? fallbackUrl : null),
      openUrl:
        toAbsoluteApiPath(version.previewPath) ||
        toAbsoluteApiPath(version.downloadPath) ||
        fallbackUrl,
      downloadUrl:
        toAbsoluteApiPath(version.downloadPath) ||
        toAbsoluteApiPath(version.previewPath) ||
        fallbackUrl,
      source: "leon-pdf",
      createdAt: version.createdAt || null,
    };
    const existing = optionsByKey.get(fileName);

    if (!existing) {
      optionsByKey.set(fileName, nextOption);
      continue;
    }

    const existingTimestamp = toComparableTimestamp(existing.createdAt);
    const nextTimestamp = toComparableTimestamp(nextOption.createdAt);
    if (
      existing.source !== "leon-pdf" ||
      nextTimestamp >= existingTimestamp
    ) {
      optionsByKey.set(fileName, {
        ...existing,
        ...nextOption,
        previewUrl: nextOption.previewUrl || existing.previewUrl,
        openUrl: nextOption.openUrl || existing.openUrl,
        downloadUrl: nextOption.downloadUrl || existing.downloadUrl,
      });
    }
  }

  return Array.from(optionsByKey.values());
};

type Props = {
  open: boolean;
  onClose: () => void;
  flightId: string;
  operatorId?: number | null;
  flightIdentifier?: string | null;
  departureAirport?: string | null;
  arrivalAirport?: string | null;
  leonTransfer?: FlightDetailLeonTransfer | null;
  onSent?: () => void;
};

export const FlightSendFilesOverlay: FunctionComponent<Props> = ({
  open,
  onClose,
  flightId,
  operatorId,
  flightIdentifier,
  departureAirport,
  arrivalAirport,
  leonTransfer,
  onSent,
}) => {
  const { data: files = [], isLoading: isLoadingFiles } = useFiles(flightId || "");
  const distributionDetailsQuery = useOpsEmailDistributionDetails(
    open
      ? {
          flightId,
          operatorId: operatorId || null,
          recipientLimit: 16,
          limit: 200,
        }
      : null,
  );
  const manualSendMutation = useTriggerOpsEmailDistributionOverride(flightId);

  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [recipientsText, setRecipientsText] = useState("");
  const [subject, setSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [reason, setReason] = useState("Manual send from flight detail page");
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState<string | null>(null);
  const [recipientsTouched, setRecipientsTouched] = useState(false);

  const recipientSuggestions = distributionDetailsQuery.data?.recipientSuggestions || [];
  const distributionEnabled = distributionDetailsQuery.data?.enabled !== false;

  const preferredDefaultFile = useMemo(() => {
    const preferredVersion = pickPreferredLeonPdfVersion(
      leonTransfer?.pdfVersions || [],
    );
    return getFileNameFromPath(preferredVersion?.filePath);
  }, [leonTransfer?.pdfVersions]);

  const sendableFileOptions = useMemo(
    () =>
      buildSendableFlightFileOptions({
        flightId,
        files,
        leonTransfer: leonTransfer || null,
      }),
    [files, flightId, leonTransfer],
  );

  const sendableFileOptionMap = useMemo(
    () =>
      new Map(
        sendableFileOptions.map((option) => [option.key, option]),
      ),
    [sendableFileOptions],
  );

  const selectedFileOptions = useMemo(
    () =>
      selectedFiles
        .map((fileKey) => sendableFileOptionMap.get(fileKey) || null)
        .filter(Boolean) as SendableFlightFileOption[],
    [selectedFiles, sendableFileOptionMap],
  );

  const parsedRecipients = useMemo(
    () => parseRecipients(recipientsText),
    [recipientsText],
  );
  const validRecipients = parsedRecipients.filter((entry) => recipientRegex.test(entry));
  const invalidRecipients = parsedRecipients.filter((entry) => !recipientRegex.test(entry));

  useEffect(() => {
    if (!open) {
      setSelectedFiles([]);
      setRecipientsTouched(false);
      setRecipientsText("");
      return;
    }

    const routeLabel = [departureAirport, arrivalAirport].filter(Boolean).join("-");
    const flightLabel = flightIdentifier || flightId;
    const defaultSubject = routeLabel
      ? `[smartOFP] Flight ${flightLabel} ${routeLabel} documents`
      : `[smartOFP] Flight ${flightLabel} documents`;

    const defaultBody = [
      "Hello,",
      "",
      `please find attached documents for flight ${flightLabel}.`,
      "",
      "Best regards,",
      "Operations Team",
    ].join("\n");

    setSubject(defaultSubject);
    setEmailBody(defaultBody);
    setSendError(null);
    setSendSuccess(null);
    setReason("Manual send from flight detail page");
  }, [open, departureAirport, arrivalAirport, flightIdentifier, flightId]);

  useEffect(() => {
    if (!open || recipientsTouched || !recipientSuggestions.length) {
      return;
    }

    const prefilled = recipientSuggestions
      .slice(0, 3)
      .map((entry) => entry.email)
      .filter(Boolean)
      .join(", ");

    if (prefilled.length) {
      setRecipientsText(prefilled);
    }
  }, [open, recipientSuggestions, recipientsTouched]);

  useEffect(() => {
    if (!open || !sendableFileOptions.length) {
      return;
    }

    if (selectedFiles.length > 0) {
      const nextSelected = selectedFiles.filter((fileKey) =>
        sendableFileOptionMap.has(fileKey),
      );
      if (nextSelected.length !== selectedFiles.length) {
        setSelectedFiles(nextSelected);
      }
      return;
    }

    const preferred =
      preferredDefaultFile && sendableFileOptionMap.has(preferredDefaultFile)
      ? preferredDefaultFile
      : null;

    if (preferred) {
      setSelectedFiles([preferred]);
      return;
    }

    const firstSummary = sendableFileOptions.find((option) =>
      /\.pdf$/i.test(option.fileName),
    );
    if (firstSummary) {
      setSelectedFiles([firstSummary.key]);
      return;
    }

    setSelectedFiles([sendableFileOptions[0].key]);
  }, [
    open,
    preferredDefaultFile,
    selectedFiles,
    sendableFileOptionMap,
    sendableFileOptions,
  ]);

  const toggleFile = (fileName: string) => {
    setSendError(null);
    setSendSuccess(null);
    setSelectedFiles((current) =>
      current.includes(fileName)
        ? current.filter((entry) => entry !== fileName)
        : [...current, fileName],
    );
  };

  const handleSend = async () => {
    if (!distributionEnabled) {
      setSendError("Email distribution is not enabled for this operator.");
      return;
    }

    if (!selectedFileOptions.length) {
      setSendError("Select at least one file.");
      return;
    }

    if (!validRecipients.length) {
      setSendError("Enter at least one valid recipient email.");
      return;
    }

    if (invalidRecipients.length) {
      setSendError(`Invalid recipients: ${invalidRecipients.join(", ")}`);
      return;
    }

    setSendError(null);
    setSendSuccess(null);

    try {
      const primaryAttachment = selectedFileOptions[0] || null;
      await manualSendMutation.mutateAsync({
        operatorId: operatorId || null,
        flightId,
        filename: primaryAttachment?.fileName || null,
        filePath: primaryAttachment?.filePath || null,
        documentType: "manual",
        recipients: validRecipients,
        sendAction: "original",
        reason: reason.trim() || "Manual send from flight detail page",
        attachments: selectedFileOptions.map((fileOption) => ({
          filename: fileOption.fileName,
          filePath: fileOption.filePath,
        })),
        emailSubject: subject.trim() || null,
        emailBody: emailBody.trim() || null,
      });

      setSendSuccess("Files queued for distribution.");
      if (onSent) {
        onSent();
      }
    } catch (error) {
      setSendError(
        error instanceof Error ? error.message : "Failed to queue file send.",
      );
    }
  };

  const handleClose = () => {
    if (manualSendMutation.isLoading) {
      return;
    }

    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Send Files</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Select one or more files, preview them, then send with editable subject and email body.
          </Typography>

          {!distributionEnabled ? (
            <Alert severity="info">
              {distributionDetailsQuery.data?.message ||
                "Email distribution is not enabled for this operator."}
            </Alert>
          ) : null}

          {distributionEnabled && distributionDetailsQuery.data?.message ? (
            <Alert severity="info">{distributionDetailsQuery.data.message}</Alert>
          ) : null}

          {isLoadingFiles ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : sendableFileOptions.length === 0 ? (
            <Alert severity="info">No files uploaded yet for that flight.</Alert>
          ) : (
            <Box
              sx={{
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                p: 1.5,
                maxHeight: 300,
                overflowY: "auto",
              }}
            >
              <FormGroup>
                {sendableFileOptions.map((fileOption) => {
                  const selected = selectedFiles.includes(fileOption.key);

                  return (
                    <Stack
                      key={`send-file-${fileOption.key}`}
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      justifyContent="space-between"
                      sx={{ py: 0.5 }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selected}
                            onChange={() => toggleFile(fileOption.key)}
                          />
                        }
                        label={
                          <Typography sx={{ wordBreak: "break-word" }}>
                            {fileOption.fileName}
                          </Typography>
                        }
                      />
                      <Stack direction="row" spacing={0.75} flexWrap="wrap">
                        <Button
                          size="small"
                          component="a"
                          href={fileOption.previewUrl || undefined}
                          target="_blank"
                          rel="noreferrer noopener"
                          disabled={!fileOption.previewUrl}
                        >
                          Preview
                        </Button>
                        <Button
                          size="small"
                          component="a"
                          href={fileOption.openUrl || undefined}
                          target="_blank"
                          rel="noreferrer noopener"
                          disabled={!fileOption.openUrl}
                        >
                          Open
                        </Button>
                        <Button
                          size="small"
                          component="a"
                          href={fileOption.downloadUrl || undefined}
                          download={fileOption.fileName}
                          disabled={!fileOption.downloadUrl}
                        >
                          Download
                        </Button>
                      </Stack>
                    </Stack>
                  );
                })}
              </FormGroup>
            </Box>
          )}

          <Typography variant="caption" color="text.secondary">
            Selected files: {selectedFileOptions.length}
          </Typography>

          {recipientSuggestions.length ? (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {recipientSuggestions.map((suggestion) => (
                <Button
                  key={`recipient-suggestion-${suggestion.email}`}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setRecipientsTouched(true);
                    const current = parseRecipients(recipientsText);
                    if (!current.includes(suggestion.email.toLowerCase())) {
                      setRecipientsText([
                        ...current,
                        suggestion.email.toLowerCase(),
                      ].join(", "));
                    }
                  }}
                >
                  {suggestion.email}
                </Button>
              ))}
            </Stack>
          ) : null}

          <TextField
            label="Recipients"
            placeholder="ops@example.com, dispatch@example.com"
            value={recipientsText}
            onChange={(event) => {
              setRecipientsTouched(true);
              setRecipientsText(event.target.value);
              setSendError(null);
            }}
            multiline
            minRows={2}
            helperText="Separate recipients by comma, semicolon, or newline."
          />

          <TextField
            label="Subject"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            fullWidth
          />

          <TextField
            label="Email body"
            value={emailBody}
            onChange={(event) => setEmailBody(event.target.value)}
            fullWidth
            multiline
            minRows={5}
          />

          <TextField
            label="Reason (optional)"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            fullWidth
          />

          {sendError ? <Alert severity="error">{sendError}</Alert> : null}
          {sendSuccess ? <Alert severity="success">{sendSuccess}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={manualSendMutation.isLoading}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={
            manualSendMutation.isLoading ||
            !selectedFileOptions.length ||
            sendableFileOptions.length === 0 ||
            !distributionEnabled
          }
        >
          {manualSendMutation.isLoading ? "Sending..." : "Send files"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
